import React from 'react';
import { CheckCircleIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { FeedbackDraft } from '../../lib/api/productFeedback';
import { ErrorMessage } from '../ui/ErrorMessage';
import { useFieldErrors } from '../../hooks/useFieldErrors';
import Button from '../ui/Button';

interface Props {
  draft: FeedbackDraft;
  onChange: (draft: FeedbackDraft) => void;
  onSubmit: () => void;
  submitting: boolean;
  error: string;
  receipt: string;
  onReset: () => void;
}

const inputClass = 'w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-300 focus:ring-2 focus:ring-vein-red-100 transition-colors';

export default function FeedbackForm({ draft, onChange, onSubmit, submitting, error, receipt, onReset }: Props) {
  const { fieldErrors, validate, clearField } = useFieldErrors<'message'>();
  const helpId = React.useId();
  const messageId = React.useId();
  const categoryId = React.useId();
  const errorId = React.useId();
  const receiptRef = React.useRef<HTMLDivElement>(null);
  const messageRef = React.useRef<HTMLTextAreaElement>(null);
  const previousReceipt = React.useRef(receipt);
  React.useEffect(() => {
    if (receipt) receiptRef.current?.focus();
    else if (previousReceipt.current) messageRef.current?.focus();
    previousReceipt.current = receipt;
  }, [receipt]);

  if (receipt) return (
    <div className="space-y-3 py-3">
      <div ref={receiptRef} tabIndex={-1} role="status" className="space-y-2 outline-none">
        <CheckCircleIcon className="w-6 h-6 text-gray-700" strokeWidth={2} aria-hidden="true" />
        <h3 className="text-lg font-semibold text-gray-900">Thanks for helping us improve Vein</h3>
        <p className="text-sm text-gray-600">Your message has been saved for our team. We can follow up at your account email.</p>
      </div>
      <Button variant="secondary" onClick={onReset}>Send another message</Button>
    </div>
  );

  return (
    <form className="space-y-4" noValidate onSubmit={event => {
      event.preventDefault();
      if (submitting || !validate({ message: !draft.message.trim() ? 'Write a message before sending.' : draft.message.length > 4000 ? 'Keep your message under 4,000 characters.' : undefined })) return;
      onSubmit();
    }}>
      {error && <ErrorMessage variant="banner" message={error} />}
      <div>
        <label htmlFor={categoryId} className="block text-xs font-medium text-gray-600 mb-1.5">What would you like to share?</label>
        <select id={categoryId} value={draft.category} disabled={submitting} onChange={event => onChange({ ...draft, category: event.target.value as FeedbackDraft['category'] })} className={inputClass}>
          <option value="comment">A comment or question</option>
          <option value="idea">An idea or suggestion</option>
          <option value="issue">Something isn’t working</option>
        </select>
      </div>
      <div>
        <label htmlFor={messageId} className="block text-xs font-medium text-gray-600 mb-1.5">Your message</label>
        <textarea ref={messageRef} id={messageId} rows={4} maxLength={4000} required disabled={submitting}
          value={draft.message} aria-invalid={!!fieldErrors.message} aria-describedby={fieldErrors.message ? `${helpId} ${errorId}` : helpId}
          onChange={event => { onChange({ ...draft, message: event.target.value }); clearField('message'); }}
          placeholder={draft.category === 'issue' ? 'What were you trying to do, and what happened instead?' : 'What’s on your mind? We’d love to hear it.'}
          className={`${inputClass} resize-y min-h-[112px]`} />
        {fieldErrors.message && <ErrorMessage variant="field" id={errorId} message={fieldErrors.message} />}
        <div className="flex justify-between gap-4 mt-2 text-xs text-gray-500">
          <p id={helpId}>Your account email and page name are included for follow-up.</p>
          <span className="whitespace-nowrap">{draft.message.length.toLocaleString()}/4,000</span>
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="submit" loading={submitting} icon={<PaperAirplaneIcon className="w-4 h-4" strokeWidth={2} />}>
          {submitting ? 'Sending…' : 'Send feedback'}
        </Button>
      </div>
    </form>
  );
}
